import { useMatch } from 'react-router-dom'

import { decodeResourcePath } from './explorer-paths'

export type ExplorerRouteSelection =
  | { kind: 'none' }
  | { kind: 'folder'; path: string }
  | { kind: 'item'; path: string }

/**
 * Read the Documents library selection from the matched path route.
 * Folder routes (`/documents/f/*`) are checked before item routes so a folder
 * path is never misread as a leaf.
 */
export function useExplorerSelection(): ExplorerRouteSelection {
  const folderMatch = useMatch({ path: '/documents/f/*', end: true })
  const itemMatch = useMatch({ path: '/documents/*', end: true })

  if (folderMatch) {
    return {
      kind: 'folder',
      path: decodeResourcePath(folderMatch.params['*'])
    }
  }

  if (itemMatch) {
    const path = decodeResourcePath(itemMatch.params['*'])
    if (path === '') return { kind: 'none' }
    // Reserved segments — handled by the templates / folder sibling routes.
    if (
      path === 'templates' ||
      path.startsWith('templates/') ||
      path === 'f' ||
      path.startsWith('f/')
    ) {
      return { kind: 'none' }
    }
    return { kind: 'item', path }
  }

  return { kind: 'none' }
}

/**
 * Read the document-templates explorer selection from `/documents/templates*`.
 * Folder routes (`…/f/*`) are checked before item routes so a folder path
 * is never misread as a leaf.
 */
export function useDocTemplateExplorerSelection(): ExplorerRouteSelection {
  const folderMatch = useMatch({ path: '/documents/templates/f/*', end: true })
  const itemMatch = useMatch({ path: '/documents/templates/*', end: true })

  if (folderMatch) {
    return {
      kind: 'folder',
      path: decodeResourcePath(folderMatch.params['*'])
    }
  }

  if (itemMatch) {
    const path = decodeResourcePath(itemMatch.params['*'])
    if (path === '' || path === 'f' || path.startsWith('f/')) return { kind: 'none' }
    return { kind: 'item', path }
  }

  return { kind: 'none' }
}
